<script setup lang="ts">
import type { MaybeElementRef } from '@vueuse/core'
import { autoUpdate, flip, offset as floatingOffset, shift as floatingShift, size, useFloating } from '@floating-ui/vue'
import { Placements } from '@ui/utils/placements'
import { onClickOutside, syncRef, unrefElement } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    show?: boolean
    reference?: MaybeElementRef
    ignore?: MaybeElementRef[]
    angle?: boolean
    offset?: boolean | number
    padding?: boolean
    placements?: Placements[]
    maxHeight?: string
    shift?: boolean
  }>(),
  {
    show: false,
    reference: null,
    ignore: () => [],
    angle: false,
    offset: false,
    padding: true,
    placements: () => [Placements.Top, Placements.Bottom],
    maxHeight: `400px`,
  },
)

defineSlots<{
  default?: (props: { close: () => void }) => void
}>()

const openModel = defineModel<boolean>('show')
const placementProp = defineModel<Placements>('placement')
const reference = computed(() => unrefElement(props.reference))

function getOffset() {
  return typeof props.offset === 'number' ? props.offset : 4
}

const floating = ref()
const initialPlacement = computed(() => props.placements[0] ?? Placements.Bottom)

const positioningMiddleware = computed(() => [
  ...(props.angle || props.offset ? [floatingOffset({ mainAxis: getOffset() })] : []),
  flip({
    padding: 8,
    rootBoundary: 'viewport',
    fallbackPlacements: props.placements.slice(1),
  }),
  ...(props.shift ? [floatingShift({ padding: 8 })] : []),
  size({
    padding: 8,
    rootBoundary: 'viewport',
    apply({ availableHeight, availableWidth, elements }) {
      elements.floating.style.setProperty('--dropdown-available-height', `${Math.max(0, availableHeight)}px`)
      elements.floating.style.setProperty('--dropdown-available-width', `${Math.max(0, availableWidth)}px`)
    },
  }),
])

const { floatingStyles, placement } = useFloating(reference, floating, {
  open: openModel,
  placement: initialPlacement,
  whileElementsMounted: autoUpdate,
  // strategy: 'fixed',
  middleware: positioningMiddleware,
})

syncRef(placement, placementProp, { direction: 'ltr' })

function close() {
  openModel.value = false
}

onClickOutside(floating, close, { ignore: props.ignore })

const classStyle = computed(() => {
  const res: string[] = []
  if (props.padding) {
    res.push('p-12')
  }
  return res
})
</script>

<template lang="pug">
Transition
  .dropdown.br-12(v-if="openModel" ref="floating" :style="floatingStyles")
    .dropdown__scroll.scrollbar-hide(:class="classStyle")
      slot(:close="close")
</template>

<style scoped>
.dropdown {
  max-width: var(--dropdown-available-width, none);
  overflow: hidden;
  box-shadow: 6px 20px 36px 0 color-mix(in srgb, var(--cue-static-neutral-inverse) 11%, transparent);
  border: 1px solid var(--cue-border-neutral-none);
  background-color: var(--cue-bg-surface-primary);
  z-index: 1000;
  /* transition: all 0.3s ease; */
  /* transition-property: transform, border, height, max-height; */
}

.dropdown__scroll {
  overflow: auto;
  max-height: min(v-bind(maxHeight), var(--dropdown-available-height, 100vh));
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
